import { Extension } from '@tiptap/core';
import type { CommandProps } from '@tiptap/core';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    indent: {
      /** Thụt các đoạn đang chọn vào thêm một bậc. */
      indent: () => ReturnType;
      /** Lùi các đoạn đang chọn ra một bậc. */
      outdent: () => ReturnType;
    };
  }
}

/** Số bậc thụt lề tối đa. */
export const maximumIndentLevel = 6;

/** Mỗi bậc thụt lề rộng bằng này, tính theo cỡ chữ — giống một lần bấm Tab trong Word. */
const emPerLevel = 2;

/** Các loại khối được thụt lề. Danh sách không nằm ở đây: danh sách thụt bằng cách lồng cấp. */
const indentableTypes = ['paragraph', 'heading'];

function clampLevel(level: number): number {
  return Math.max(0, Math.min(maximumIndentLevel, Math.round(level)));
}

/** Đọc bậc thụt lề từ thuộc tính `margin-left` dạng `4em`; đơn vị khác coi như không thụt. */
function parseLevel(element: HTMLElement): number {
  const match = /^(\d+(?:\.\d+)?)em$/.exec(element.style.marginLeft.trim());

  return match === null ? 0 : clampLevel(Number(match[1]) / emPerLevel);
}

/** Lệnh dời các đoạn đang chọn đi một số bậc; không đoạn nào đổi thì báo không làm được gì. */
function shiftIndent(delta: number) {
  return () =>
    ({ tr, state, dispatch }: CommandProps): boolean => {
      // Đếm qua một mảng: biến đơn gán lại bên trong hàm gọi lại thì bộ kiểm kiểu không theo dõi được.
      const shifted: number[] = [];

      state.doc.nodesBetween(state.selection.from, state.selection.to, (node, position) => {
        if (!indentableTypes.includes(node.type.name)) {
          return true;
        }

        const current: unknown = node.attrs.indent;
        const level = clampLevel((typeof current === 'number' ? current : 0) + delta);

        if (level !== current) {
          tr.setNodeMarkup(position, undefined, { ...node.attrs, indent: level });
          shifted.push(position);
        }

        return false;
      });

      if (shifted.length > 0 && dispatch !== undefined) {
        dispatch(tr);
      }

      return shifted.length > 0;
    };
}

/**
 * Thụt lề cho đoạn văn và tiêu đề mục — lưu thành `margin-left` theo bậc, đúng kiểu duy nhất (cùng với
 * căn lề) mà máy chủ giữ lại khi làm sạch nội dung.
 *
 * Cần cho việc nhập từ Word: tài liệu Word hay thụt lề đoạn văn, và người viết muốn bài trên website giữ
 * đúng bố cục đó.
 */
export const Indent = Extension.create({
  name: 'indent',

  addGlobalAttributes() {
    return [
      {
        types: indentableTypes,
        attributes: {
          indent: {
            default: 0,
            parseHTML: (element: HTMLElement) => parseLevel(element),
            renderHTML: (attributes: Record<string, unknown>) => {
              const level = typeof attributes.indent === 'number' ? attributes.indent : 0;

              return level > 0 ? { style: `margin-left: ${(level * emPerLevel).toString()}em` } : {};
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      indent: shiftIndent(1),
      outdent: shiftIndent(-1),
    };
  },
});
